import React from "react";
import type { ApolloClient } from "@apollo/client";
import JSONPretty from "react-json-pretty";
import * as Collapsible from "@radix-ui/react-collapsible";
import Button from "components/Button";

import { isEmpty, map } from "lodash";
import SimpleModal from "components/SimpleModal";
import IconTableHeader from "components/IconTableHeader";
import IconTable from "components/IconTable";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { getKycStatus } from "actions/adminActions";
import IconSpinner from "components/IconSpinner";
import Alert from "components/Alert";
import { createErrorSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import type { GetKybStatusQuery } from "types/graphql";
import type { RootState } from "types/state";

// The list element type stored in state and rendered per-row. Codegen types
// each element as nullable, but the original code unconditionally treats each
// item as a non-null object (passed to IconTable, which requires `T extends
// object`), so the per-row type is narrowed to the non-null element shape.
type KycResponse = NonNullable<
  NonNullable<
    NonNullable<GetKybStatusQuery["getKybStatus"]>["kycResponses"]
  >[number]
>;

const errorSelector = createErrorSelector(adminConstants.GET_KYC_STATUS);

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
});

const mapDispatchToProps = {
  getKycStatus,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Optional to match the parent (KYB/Summary.tsx) where companyId is `string |
  // undefined`; narrowed with `as string` at the action call site, matching the
  // parent's own `this.props.companyId as string` convention.
  companyId?: string;
  onClose: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  isFetching: boolean;
  kycResponses: NonNullable<GetKybStatusQuery["getKybStatus"]>["kycResponses"];
}

class KycJsonViewer extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isFetching: true,
      kycResponses: [],
    };
  }

  async componentDidMount() {
    // dispatchFullLifecycle resolves to a union of the SUCCESS action
    // ({ data }) and the FAILURE action ({ error }); the original JS
    // unconditionally destructured `data` (the success shape), so narrow to
    // that member at the boundary. Behavior is identical: on the failure
    // branch `data` is `undefined`, exactly as in the untyped JS.
    const { data: kycStatus } = (await this.props.getKycStatus(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    )) as { data: NonNullable<GetKybStatusQuery["getKybStatus"]> };

    this.setState({
      isFetching: false,
      kycResponses: kycStatus.kycResponses,
    });
  }

  buildMemberTrigger = (data: KycResponse) => {
    const columns = [
      {
        label: "Entity Type",
        key: "rawResponse.entityType",
      },
      {
        label: "Status",
        key: "rawResponse.status",
      },
      {
        label: "Message",
        key: "rawResponse.message",
      },
      {
        label: "Metadata",
        customComponent: () => {
          return (
            <div className="employerInfo-action">
              <Button name="action" size="sm" btnLabel="View Results" />
            </div>
          );
        },
      },
    ];
    return (
      <>
        <IconTableHeader tableHeader="Status" />
        <IconTable columns={columns} data={[data]} />
      </>
    );
  };

  buildJsonView = () => {
    if (this.state.isFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert type="error" msg={this.props.error} />;
    }

    if (isEmpty(this.state.kycResponses)) {
      return <div>No KYB/KYC status available</div>;
    }

    return map(this.state.kycResponses, (item, index) => {
      return (
        <div key={index} style={{ marginBottom: 24 }}>
          <Collapsible.Root>
            <Collapsible.Trigger asChild>
              {/* codegen types each element as nullable; the original code
                  always renders it as a non-null object — narrow at the use. */}
              <div>{this.buildMemberTrigger(item as KycResponse)}</div>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <JSONPretty
                data={item}
                themeClassName="custom-json-pretty"
                style={{ fontSize: "14px", margin: 15 }}
                mainStyle="background:#F7F7F7; line-height:1.3; padding: 20px; ;overflow:auto; color:#999;"
                keyStyle="color:#905;"
                stringStyle="color:#690;"
                valueStyle="color:#905;"
                booleanStyle="color:#905;"
              ></JSONPretty>
            </Collapsible.Content>
          </Collapsible.Root>
        </div>
      );
    });
  };

  render() {
    return (
      <SimpleModal
        show={true}
        size={"lg"}
        onClose={() => this.props.onClose()}
        title="KYB/KYC Details"
        buttonText="close"
      >
        {this.buildJsonView()}
      </SimpleModal>
    );
  }
}

export default connector(withApollo<Props>(KycJsonViewer));
